import React, { useState, useEffect, memo } from 'react';
import { Trash2, Info } from 'lucide-react';
import { Box, Typography, Pressable, Input } from '@/shared/design-system/primitives';
import { TextStyle, ViewStyle } from 'react-native';
import { formatMacro } from '@shared/utils/formatting';
import { EVENT_TYPES, EVENT_THEMES } from '@/shared/constants/event-types.v4';
import { DiagnosticCard } from '../../shared/DiagnosticCard';

interface MetabolicLedgerRowProps {
    item: any;
    onUpdate: (id: string, field: string, value: any) => void;
    onUpdateItem: (id: string, updates: any) => void;
    onRemove: (id: string) => void;
}

/**
 * MetabolicLedgerRow: Dynamic nutrition item editor.
 * V6.4: Removed custom memo comparison to ensure stable handlers work correctly.
 * HARDENED: Uses standard memo for high performance with correct reactivity.
 */
export const MetabolicLedgerRow = memo(({ 
    item, onUpdate, onUpdateItem, onRemove 
}: MetabolicLedgerRowProps) => {
    const themeColor = EVENT_THEMES[EVENT_TYPES.NUTRITION];
    const [localQty, setLocalQuantity] = useState(String(item.numeric_amount || item.quantity || "").replace(/[^0-9.]/g, '') || "0");
    const [isInfoExpanded, setIsInfoExpanded] = useState(false);

    useEffect(() => {
        setLocalQuantity(String(item.numeric_amount || item.quantity || "").replace(/[^0-9.]/g, '') || "0");
    }, [item.quantity, item.numeric_amount]);

    const macroPillStyle = (color: string): ViewStyle => ({
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: `${color}15`, borderWidth: 1, borderColor: `${color}33`,
        borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4
    });

    const stealthPillStyle = (color: string = '#222'): ViewStyle => ({
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(255,255,255,0.02)', borderWidth: 1, borderColor: color,
        borderRadius: 6, paddingHorizontal: 6, paddingVertical: 3
    });

    const miniInputStyle: TextStyle = {
        color: '#fff', fontSize: 12, fontWeight: '600', textAlign: 'center', width: 30, padding: 0
    };

    const commitQuantityChange = () => {
        const val = parseFloat(localQty);
        if (isNaN(val) || val <= 0) {
            setLocalQuantity(String(item.quantity || "").replace(/[^0-9.]/g, ''));
            return;
        }
        const oldQty = Number(item.numeric_amount) || parseFloat(String(item.quantity || "0").replace(/[^0-9.]/g, '')) || 1;
        if (val === oldQty) return;
        const multiplier = val / oldQty;
        const unit = item.unit || String(item.quantity || "g").match(/[a-zA-Z]+/) || 'g';
        const updates: any = { quantity: `${val}${unit}`, numeric_amount: val };
        
        const macros = ['calories', 'protein', 'carbs', 'fat', 'insulin_load'];
        macros.forEach(m => {
            if (item[m]) updates[m] = m === 'calories' ? Math.round(item[m] * multiplier) : Number((item[m] * multiplier).toFixed(1));
        });

        if (item.micros) {
            const newMicros = { ...item.micros };
            Object.keys(newMicros).forEach(key => { 
                if (typeof newMicros[key] === 'number') {
                    newMicros[key] = Number((newMicros[key] * multiplier).toFixed(1)); 
                } else if (typeof newMicros[key] === 'object' && newMicros[key] !== null && 'amount' in newMicros[key]) {
                    newMicros[key] = {
                        ...newMicros[key],
                        amount: Number((newMicros[key].amount * multiplier).toFixed(1))
                    };
                }
            });

            // 1. Scale Discovered Micros (Legacy AI Results)
            if (newMicros.discovered_micros && Array.isArray(newMicros.discovered_micros)) {
                newMicros.discovered_micros = newMicros.discovered_micros.map((dm: any) => ({
                    ...dm,
                    amount: Number((Number(dm.amount) * multiplier).toFixed(1))
                }));
            }
            updates.micros = newMicros;
        }
        onUpdateItem(item.id, updates);
    };

    return (
        <Box style={{ marginBottom: 4 }}>
            <Box style={{ 
                padding: 12, borderRadius: 14, 
                backgroundColor: `${themeColor}12`, 
                borderWidth: 1, borderColor: isInfoExpanded ? themeColor : `${themeColor}40`,
                display: 'flex', flexDirection: 'column', gap: 8,
                boxShadow: isInfoExpanded ? `0 0 25px ${themeColor}22` : `0 4px 20px rgba(0,0,0,0.4)`,
                transition: 'all 0.2s ease'
            } as any}>
                {/* TIER 1: IDENTITY & COMMAND */}
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', overflow: 'hidden' }}>
                    <Input 
                        value={item.item_name} 
                        onChangeText={(text) => onUpdate(item.id, 'item_name', text)} 
                        style={{ flex: 1, flexShrink: 1, minWidth: 0, fontSize: 14, fontWeight: '900', color: '#fff', borderBottomWidth: 0, padding: 0, backgroundColor: 'transparent' } as any} 
                    />
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <Box style={{ backgroundColor: '#0a0a0a', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#222' }}>
                            <Input keyboardType="numeric" value={localQty} onChangeText={setLocalQuantity} onBlur={commitQuantityChange} style={{ width: 35, textAlign: 'right', fontSize: 12, fontWeight: '950', color: '#fff', borderBottomWidth: 0, padding: 0 } as any} />
                            <Typography style={{ fontSize: 9, color: '#666', fontWeight: 'bold', marginLeft: 2 }}>{item.unit || 'g'}</Typography>
                        </Box>
                        <Pressable onPress={() => onRemove(item.id)} style={{ padding: 4, opacity: 0.4 }}><Trash2 size={14} color="#fff" /></Pressable>
                    </Box>
                </Box>

                {/* TIER 2: PRIMARY ENERGY */}
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                        <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 }}>
                            <Input keyboardType="numeric" value={formatMacro(item.calories)} onChangeText={(t) => onUpdate(item.id, 'calories', t)} style={{ ...miniInputStyle, width: 40, color: '#f29b11' } as any} />
                            <Typography style={{ fontSize: 9, color: '#f29b11', opacity: 0.6 }}>KC</Typography>
                        </Box>
                        <Box style={macroPillStyle('#3b82f6')}><Input keyboardType="numeric" value={formatMacro(item.protein)} onChangeText={(t) => onUpdate(item.id, 'protein', t)} style={miniInputStyle as any} /><Typography style={{ fontSize: 9, color: '#3b82f6', fontWeight: '900' }}>P</Typography></Box>
                        <Box style={macroPillStyle('#2ecc71')}><Input keyboardType="numeric" value={formatMacro(item.carbs)} onChangeText={(t) => onUpdate(item.id, 'carbs', t)} style={miniInputStyle as any} /><Typography style={{ fontSize: 9, color: '#2ecc71', fontWeight: '900' }}>C</Typography></Box>
                        <Box style={macroPillStyle('#ef4444')}><Input keyboardType="numeric" value={formatMacro(item.fat)} onChangeText={(t) => onUpdate(item.id, 'fat', t)} style={miniInputStyle as any} /><Typography style={{ fontSize: 9, color: '#ef4444', fontWeight: '900' }}>F</Typography></Box>
                    </Box>

                    {/* UNIFIED TACTICAL INFO BUTTON */}
                    <Pressable 
                        onPress={() => setIsInfoExpanded(!isInfoExpanded)} 
                        style={{ 
                            width: 36, height: 34, borderRadius: 12, 
                            backgroundColor: isInfoExpanded ? `${themeColor}22` : 'rgba(255,255,255,0.03)', 
                            borderWidth: 1, borderColor: isInfoExpanded ? themeColor : `${themeColor}33`,
                            alignItems: 'center', justifyContent: 'center'
                        }}
                    >
                        <Info size={16} color={isInfoExpanded ? '#fff' : themeColor} />
                    </Pressable>
                </Box>

                {/* TIER 3: METABOLIC ESSENTIALS */}
                <Box style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {(item.food_insulin_index || item.glycemic_index) && (
                        <Box style={macroPillStyle('#f1c40f')}>
                            <Input keyboardType="numeric" value={String(item.food_insulin_index || item.glycemic_index)} onChangeText={(t) => onUpdate(item.id, 'food_insulin_index', t)} style={miniInputStyle as any} />
                            <Typography style={{ fontSize: 9, color: '#f1c40f', fontWeight: '900' }}>FII</Typography>
                        </Box>
                    )}
                    {item.micros?.fiber > 0 && <Box style={stealthPillStyle('#444')}><Typography style={{ fontSize: 8, color: '#666', fontWeight: '900' }}>FIB</Typography><Typography style={{ fontSize: 10, color: '#ccc', fontWeight: 'bold' }}>{item.micros.fiber}g</Typography></Box>}
                    {item.micros?.leucine > 0 && <Box style={stealthPillStyle('#444')}><Typography style={{ fontSize: 8, color: '#666', fontWeight: '900' }}>LEU</Typography><Typography style={{ fontSize: 10, color: '#ccc', fontWeight: 'bold' }}>{item.micros.leucine}mg</Typography></Box>}
                    {item.micros?.sodium > 0 && <Box style={stealthPillStyle('#444')}><Typography style={{ fontSize: 8, color: '#666', fontWeight: '900' }}>SOD</Typography><Typography style={{ fontSize: 10, color: '#ccc', fontWeight: 'bold' }}>{item.micros.sodium}mg</Typography></Box>}
                </Box>

                {/* INLINE DIAGNOSTIC CARD */}
                <DiagnosticCard data={item.micros || {}} isVisible={isInfoExpanded} themeColor={themeColor} />
            </Box>
        </Box>
    );
});
